import { createPortal } from 'react-dom';
import { Clock, LogOut } from 'lucide-react';

interface SessionTimeoutModalProps {
  visible: boolean;
  remainingSeconds: number;
  onContinue: () => void;
  onSignOut: () => void;
}

export function SessionTimeoutModal({ visible, remainingSeconds, onContinue, onSignOut }: SessionTimeoutModalProps) {
  if (!visible) return null;

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Overlay — does NOT close on click */}
      <div className="absolute inset-0 bg-gray-900/60 dark:bg-black/70" />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full mx-4 p-6">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
            <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Session Expiring
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
            Your session will expire in
          </p>

          {/* Countdown */}
          <p className="text-3xl font-mono font-bold text-amber-600 dark:text-amber-400 mb-4">
            {timeDisplay}
          </p>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Would you like to continue your session?
          </p>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <button
              onClick={onContinue}
              className="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-lg transition-colors"
            >
              Continue Session
            </button>
            <button
              onClick={onSignOut}
              className="w-full inline-flex justify-center items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
